import "./AdminAnalyticsPage.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../../components/Header/Header";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { ONBOARDING_SOURCES } from "../Onboarding/OnboardingPage";
import { UserApi } from "../../services/api/user";
import { OnboardingSourceStatsResponse } from "../../types/types";
import { isAdminRole } from "../../utils/roles";

const LABEL_BY_SOURCE = Object.fromEntries(ONBOARDING_SOURCES.map((item) => [item.value, item.label]));

const labelOf = (source: string) => LABEL_BY_SOURCE[source] ?? source;

export const AdminAnalyticsPage = () => {
  const { user } = useCurrentUser();

  const [stats, setStats] = useState<OnboardingSourceStatsResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canAccess = useMemo(() => isAdminRole(user?.role), [user?.role]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await UserApi.getOnboardingStats();
      setStats(response);
    } catch (requestError) {
      const message =
        requestError instanceof Error ? requestError.message : "Не удалось загрузить аналитику. Попробуйте еще раз.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) {
      return;
    }

    void fetchStats();
  }, [canAccess, fetchStats]);

  if (!canAccess) {
    return (
      <>
        <Header />
        <main className="admin-analytics">
          <section className="admin-analytics__card">
            <p className="admin-analytics__empty">Доступ к этому разделу есть только у администратора.</p>
          </section>
        </main>
      </>
    );
  }

  const total = stats.reduce((sum, item) => sum + item.count, 0);

  return (
    <>
      <Header />
      <main className="admin-analytics">
        <section className="admin-analytics__card">
          <h1 className="admin-analytics__title">Аналитика</h1>
          <p className="admin-analytics__subtitle">
            Источники, из которых пользователи узнали о сервисе. Ответов: {total} (один пользователь может выбрать несколько).
          </p>

          {error ? <p className="admin-analytics__error">{error}</p> : null}

          {loading ? (
            <p className="admin-analytics__empty">Загрузка...</p>
          ) : stats.length === 0 ? (
            <p className="admin-analytics__empty">Пока нет ответов.</p>
          ) : (
            <table className="admin-analytics__table">
              <thead>
                <tr>
                  <th>Источник</th>
                  <th>Количество</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((item) => (
                  <tr key={item.source}>
                    <td>{labelOf(item.source)}</td>
                    <td>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </>
  );
};